import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Check, CheckCircle2, LayoutTemplate, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { ETAPAS, etapaInfo, fmtData, SAUDE, STATUS_CICLO, PRIORIDADES, STATUS_GRAVACAO, TIPOS_EVENTO } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TarefaEditDialog } from "@/components/TarefaEditDialog";

const ORIGEM_CICLO = {
  retomada: "Novo ciclo após a reativação do cliente",
  manual: "Criado manualmente",
  geracao_mensal: "Gerado na virada do mês (automação / botão Gerar ciclos)",
  renovacao_automatica: "Renovação automática ao concluir o ciclo anterior",
  venda: "Criado pela venda ganha no Comercial",
  automatica: "Gerado por automação",
};

export function CicloDrawer({ cicloId, onClose, onChange, equipe, templates }) {
  const [ciclo, setCiclo] = useState(null);
  const [novaTarefa, setNovaTarefa] = useState({ titulo: "", responsavel: "", prazo: "" });
  const [tarefaEditando, setTarefaEditando] = useState(null);
  const [salvandoTarefa, setSalvandoTarefa] = useState(false);

  const load = useCallback(() => cicloId ? api.get(`/ciclos/${cicloId}`).then((r) => setCiclo(r.data)).catch((err) => toast.error(apiError(err))) : Promise.resolve(), [cicloId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (updates) => {
    const res = await api.patch(`/ciclos/${cicloId}`, updates);
    setCiclo((c) => ({ ...c, ...res.data }));
    onChange();
  };

  const addTarefa = async (e) => {
    e.preventDefault();
    if (!novaTarefa.titulo.trim()) return;
    await api.post("/tarefas", { ...novaTarefa, ciclo_id: cicloId, cliente_id: ciclo.cliente_id, etapa: ciclo.etapa });
    setNovaTarefa({ titulo: "", responsavel: "", prazo: "" });
    load(); onChange();
  };

  const toggleTarefa = async (t) => {
    if (salvandoTarefa) return;
    setSalvandoTarefa(true);
    try {
      await api.patch(`/tarefas/${t.id}`, { status: t.status === "concluido" ? "em_andamento" : "concluido" });
      await load();
      onChange();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSalvandoTarefa(false);
    }
  };

  const delTarefa = async (t) => { await api.delete(`/tarefas/${t.id}`); load(); onChange(); };

  const tarefaSalva = () => {
    load();
    onChange();
  };

  const concluir = async () => {
    const res = await api.post(`/ciclos/${cicloId}/concluir`);
    toast.success(res.data.proximo_ciclo ? "Ciclo concluído — próximo ciclo criado automaticamente" : "Ciclo concluído");
    load(); onChange();
  };

  if (!ciclo) return <Sheet open={!!cicloId} onOpenChange={onClose}><SheetContent className="bg-[#0e0e11] border-l border-[#27272A]"><SheetTitle className="sr-only">Ciclo</SheetTitle></SheetContent></Sheet>;

  const et = etapaInfo(ciclo.etapa);
  const st = STATUS_CICLO[ciclo.status] || STATUS_CICLO.em_andamento;
  const sd = SAUDE[ciclo.saude] || SAUDE.saudavel;
  const historico = ciclo.status === "cancelado" || !!ciclo.cliente_encerrado_em;

  return (
    <>
      <Sheet open={!!cicloId} onOpenChange={onClose}>
        <SheetContent className="bg-[#0e0e11] border-l border-[#27272A] w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-[#F4F4F0] pr-8">{ciclo.titulo}</SheetTitle>
          <SheetDescription className="sr-only">{historico ? "Histórico do ciclo e atividades, somente para consulta." : "Detalhes do ciclo e atividades editáveis."}</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-5" data-testid="ciclo-drawer">
          {historico && <p role="status" data-testid="ciclo-historico-aviso" className="rounded-lg border border-[#71717A]/40 p-3 text-sm text-[#A1A1AA]">Este ciclo pertence a um período encerrado e está disponível somente para consulta. Tarefas canceladas não contam como concluídas. A reativação do cliente inicia um novo ciclo.</p>}
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border" style={{ color: et.cor, borderColor: `${et.cor}55` }} data-testid="ciclo-etapa">{et.nome}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border" style={{ color: st.cor, borderColor: `${st.cor}55` }} data-testid="ciclo-status">{st.nome}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border" style={{ color: sd.cor, borderColor: `${sd.cor}55` }} data-testid="ciclo-saude">{sd.nome}</span>
            <span className="font-mono text-[10px] text-[#71717A] uppercase">{ciclo.competencia}</span>
          </div>

          {(ciclo.origem || ciclo.template_id) && (
            <div className="border border-[#27272A] rounded-lg px-4 py-3 flex items-center gap-3" data-testid="ciclo-origem">
              <LayoutTemplate size={14} className="text-[#8CC63F] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#E4E4E0]">{ORIGEM_CICLO[ciclo.origem] || ciclo.origem}</p>
                <p className="caption-label normal-case tracking-normal mt-0.5">
                  {ciclo.template_id ? `Tarefas geradas pelo template "${templates.find((t) => t.id === ciclo.template_id)?.nome || "removido"}"` : "Sem template — tarefas criadas manualmente"}
                </p>
              </div>
              <Link to="/configuracoes" data-testid="ciclo-origem-templates" className="font-mono text-[10px] text-[#8CC63F] hover:text-[#A9DD6B] shrink-0 uppercase tracking-wide">Templates</Link>
            </div>
          )}

          {ciclo.proxima_acao && (
            <div className="border border-[#8CC63F]/30 bg-[#8CC63F]/5 rounded-lg px-4 py-3" data-testid="ciclo-proxima-acao">
              <p className="caption-label text-[#8CC63F]">Próxima ação</p>
              <p className="text-sm text-[#F4F4F0] mt-1">{ciclo.proxima_acao.titulo}</p>
              <p className="caption-label normal-case tracking-normal mt-1">{ciclo.proxima_acao.responsavel || "sem responsável"} · {fmtData(ciclo.proxima_acao.prazo)}</p>
            </div>
          )}

          <fieldset disabled={historico} className="min-w-0 space-y-5" data-testid="ciclo-edicao-campos">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label htmlFor="ciclo-titulo" className="caption-label">Nome do ciclo</Label>
              <Input id="ciclo-titulo" key={`titulo-${ciclo.id}-${ciclo.titulo}`} defaultValue={ciclo.titulo} onBlur={(e) => { const titulo = e.target.value.trim(); if (titulo && titulo !== ciclo.titulo) patch({ titulo }).catch((error) => toast.error(apiError(error))); }} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            <div><Label htmlFor="ciclo-competencia" className="caption-label">Mês do ciclo</Label>
              <Input id="ciclo-competencia" data-testid="ciclo-competencia" type="month" value={ciclo.competencia || ""} onChange={(e) => { if (/^\d{4}-(0[1-9]|1[0-2])$/.test(e.target.value)) patch({ competencia: e.target.value }).catch((error) => toast.error(apiError(error))); }} className="mt-1.5 bg-[#18181C] border-[#27272A]" />
              <p className="mt-2 text-xs text-[#A1A1AA]">Organiza o ciclo no mês escolhido. As datas das atividades continuam individuais.</p></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="caption-label">Etapa</Label>
              <Select value={ciclo.etapa} onValueChange={(v) => patch({ etapa: v })}>
                <SelectTrigger data-testid="ciclo-etapa-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  {ETAPAS.map((e) => <SelectItem key={e.key} value={e.key}>{e.nome}</SelectItem>)}
                </SelectContent>
              </Select>
              {!historico && ciclo.status !== "concluido" && <p className="mt-2 text-xs text-[#A1A1AA]">Ao concluir ou reabrir tarefas, a etapa acompanha o que ainda falta fazer.</p>}
            </div>
            <div><Label className="caption-label">Status</Label>
              <Select value={ciclo.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger data-testid="ciclo-status-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  {Object.entries(STATUS_CICLO).filter(([k]) => k !== "cancelado" || historico).map(([k, v]) => <SelectItem key={k} value={k}>{v.nome}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="caption-label">Responsável</Label>
              <Select value={ciclo.responsavel || "none"} onValueChange={(v) => patch({ responsavel: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ciclo-responsavel-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  <SelectItem value="none">—</SelectItem>
                  {equipe.map((m) => <SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="caption-label">Prioridade</Label>
              <Select value={ciclo.prioridade} onValueChange={(v) => patch({ prioridade: v })}>
                <SelectTrigger data-testid="ciclo-prioridade-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  {Object.entries(PRIORIDADES).map(([k, v]) => <SelectItem key={k} value={k}>{v.nome}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>

          <div className="border border-[#8CC63F]/25 bg-[#8CC63F]/5 rounded-xl p-4 space-y-3" data-testid="ciclo-gravacao-sincronizada">
            <div>
              <p className="caption-label text-[#8CC63F]">Gravação sincronizada</p>
              <p className="text-[11px] text-[#71717A] mt-1">As alterações aparecem automaticamente em Produção → Gravações e no Google Agenda quando a integração estiver ativa.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label className="caption-label">Data da gravação</Label>
                <Input data-testid="ciclo-captacao_prevista" type="date" value={ciclo.captacao_prevista || ""} onChange={(e) => patch({ captacao_prevista: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Tipo</Label>
                <Select value={ciclo.captacao_tipo || "captacao_externa"} onValueChange={(v) => patch({ captacao_tipo: v })}>
                  <SelectTrigger data-testid="ciclo-captacao-tipo" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    {TIPOS_EVENTO.filter((t) => ["gravacao_estudio", "captacao_externa"].includes(t.key)).map((t) => <SelectItem key={t.key} value={t.key}>{t.nome}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="caption-label">Início</Label>
                <Input data-testid="ciclo-captacao-inicio" type="time" value={ciclo.captacao_hora_inicio || ""} onChange={(e) => patch({ captacao_hora_inicio: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Fim</Label>
                <Input data-testid="ciclo-captacao-fim" type="time" value={ciclo.captacao_hora_fim || ""} onChange={(e) => patch({ captacao_hora_fim: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Local</Label>
                <Input key={ciclo.captacao_local || "local-vazio"} data-testid="ciclo-captacao-local" defaultValue={ciclo.captacao_local || ""} onBlur={(e) => patch({ captacao_local: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Estúdio ou externa" /></div>
              <div><Label className="caption-label">Status</Label>
                <Select value={ciclo.captacao_status || "planejando"} onValueChange={(v) => patch({ captacao_status: v, captacao_confirmada: ["confirmada", "realizada"].includes(v) })}>
                  <SelectTrigger data-testid="ciclo-captacao-status" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    {Object.entries(STATUS_GRAVACAO).map(([k, v]) => <SelectItem key={k} value={k}>{v.nome}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {[["entrega_prevista", "Entrega prevista"], ["publicacao_prevista", "Publicação prevista"], ["inicio_previsto", "Início previsto"]].map(([k, l]) => (
              <div key={k}><Label className="caption-label">{l}</Label>
                <Input data-testid={`ciclo-${k}`} type="date" value={ciclo[k] || ""} onChange={(e) => patch({ [k]: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            ))}
          </div>

          <div>
            <p className="caption-label mb-3">Tarefas · {ciclo.progresso}%</p>
            <div className="h-1 bg-[#27272A] rounded-full overflow-hidden mb-3">
              <div className="h-full rounded-full transition-all" style={{ width: `${ciclo.progresso}%`, backgroundColor: et.cor }} />
            </div>
            <div className="space-y-2">
              {(ciclo.tarefas || []).map((t) => {
                const atrasada = !historico && t.prazo && t.prazo < new Date().toISOString().slice(0, 10) && !["concluido", "cancelado"].includes(t.status);
                return (
                  <div key={t.id} data-testid={`tarefa-${t.id}`} className={`flex items-center gap-3 border rounded-lg px-3 py-2.5 ${t.status === "concluido" ? "border-[#4ADE80]/20 bg-[#4ADE80]/5" : atrasada ? "border-[#F43F5E]/30" : "border-[#27272A]"}`}>
                    <button type="button" data-testid={`tarefa-toggle-${t.id}`} onClick={() => toggleTarefa(t)} disabled={salvandoTarefa} aria-label={`${t.status === "concluido" ? "Reabrir" : "Concluir"} ${t.titulo}`} aria-pressed={t.status === "concluido"} className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 disabled:opacity-50 ${t.status === "concluido" ? "bg-[#4ADE80] border-[#4ADE80]" : "border-[#3F3F46]"}`}>
                      {t.status === "concluido" && <Check size={11} className="text-[#09090B]" />}
                    </button>
                    <button type="button" onClick={() => setTarefaEditando(t)} className="flex-1 min-w-0 text-left" data-testid={`tarefa-editar-conteudo-${t.id}`}>
                      <p className={`text-xs ${t.status === "concluido" ? "text-[#71717A] line-through" : "text-[#E4E4E0]"}`}>{t.titulo}</p>
                      <p className="caption-label normal-case tracking-normal">{t.responsavel || "—"}{t.prazo ? ` · ${fmtData(t.prazo)}` : ""}{t.status === "cancelado" ? " · Cancelada" : atrasada ? " · atrasada" : ""}</p>
                    </button>
                    <button type="button" data-testid={`tarefa-editar-${t.id}`} onClick={() => setTarefaEditando(t)} className="text-[#71717A] hover:text-[#8CC63F]" title="Editar atividade" aria-label={`Editar ${t.titulo}`}><Pencil size={13} /></button>
                    <button data-testid={`tarefa-excluir-${t.id}`} onClick={() => delTarefa(t)} className="text-[#3F3F46] hover:text-[#F43F5E]"><Trash2 size={13} /></button>
                  </div>
                );
              })}
            </div>
            {!historico && <form onSubmit={addTarefa} className="mt-3 grid gap-2 sm:grid-cols-[1fr_110px_120px_auto]">
              <Input data-testid="nova-tarefa-titulo" value={novaTarefa.titulo} onChange={(e) => setNovaTarefa({ ...novaTarefa, titulo: e.target.value })} placeholder="Nova tarefa…" className="bg-[#18181C] border-[#27272A] h-9 text-xs" />
              <Select value={novaTarefa.responsavel || "none"} onValueChange={(v) => setNovaTarefa({ ...novaTarefa, responsavel: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="nova-tarefa-responsavel" className="bg-[#18181C] border-[#27272A] h-9 text-xs"><SelectValue placeholder="Resp." /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  <SelectItem value="none">—</SelectItem>
                  {equipe.map((m) => <SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input data-testid="nova-tarefa-prazo" type="date" value={novaTarefa.prazo} onChange={(e) => setNovaTarefa({ ...novaTarefa, prazo: e.target.value })} className="bg-[#18181C] border-[#27272A] h-9 text-xs" />
              <Button data-testid="nova-tarefa-add" type="submit" className="w-full shrink-0 bg-[#8CC63F]/15 text-[#8CC63F] border border-[#8CC63F]/30 hover:bg-[#8CC63F]/25 sm:h-9 sm:w-9 sm:px-0"><Plus size={15} /><span className="ml-2 sm:hidden">Adicionar tarefa</span></Button>
            </form>}
          </div>

          {!historico && ciclo.status !== "concluido" && (
            <Button data-testid="ciclo-concluir-button" onClick={concluir} className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">
              <CheckCircle2 size={15} className="mr-1" /> Concluir ciclo
            </Button>
          )}
          </fieldset>
          </div>
        </SheetContent>
      </Sheet>
      <TarefaEditDialog
        tarefa={tarefaEditando}
        equipe={equipe}
        onOpenChange={(open) => { if (!open) setTarefaEditando(null); }}
        onSaved={tarefaSalva}
      />
    </>
  );
}
